import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "@/core/api/crmApi";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import type {
  CreateRetailDiscountCodeRequest,
  CreateRetailGiftCardRequest,
  RetailDiscountCode,
  RetailGiftCard,
  UpdateRetailLoyaltyProgramRequest,
} from "@/core/types/tenantWorkspace";
import { getErrorMessage } from "@/core/utils/errors";
import { formatDateTime } from "@/core/utils/date";
import { AppIcon } from "@/shared/components/AppIcon";
import { JalaliDateTimePicker } from "@/shared/components/JalaliDateTimePicker";

const number = (value: number): string =>
  new Intl.NumberFormat("fa-IR").format(value);
const money = (value: number, currency: string): string =>
  `${number(value)} ${currency === "IRR" ? "ریال" : currency}`;
const emptyCode = (): CreateRetailDiscountCodeRequest => ({
  code: "",
  name: "",
  customerId: null,
  discountType: "Percentage",
  value: 10,
  maximumDiscountAmount: null,
  minimumOrderAmount: 0,
  currency: "IRR",
  startsAtUtc: null,
  expiresAtUtc: null,
  maximumRedemptions: null,
  isActive: true,
});
const emptyCard = (): CreateRetailGiftCardRequest => ({
  code: "",
  customerId: null,
  initialBalance: 0,
  currency: "IRR",
  expiresAtUtc: null,
  isActive: true,
});

export const RetailLoyaltyPage = (): React.ReactElement => {
  const client = useQueryClient();
  const program = useQuery({
    queryKey: ["retail", "loyalty", "program"],
    queryFn: tenantWorkspaceApi.retailLoyaltyProgram,
  });
  const codes = useQuery({
    queryKey: ["retail", "loyalty", "codes"],
    queryFn: tenantWorkspaceApi.retailDiscountCodes,
  });
  const cards = useQuery({
    queryKey: ["retail", "loyalty", "cards"],
    queryFn: tenantWorkspaceApi.retailGiftCards,
  });
  const customers = useQuery({
    queryKey: ["contacts", "loyalty-picker"],
    queryFn: () => crmApi.contacts({ page: 1, pageSize: 100 }),
  });
  const [settings, setSettings] =
    useState<UpdateRetailLoyaltyProgramRequest | null>(null);
  const [code, setCode] = useState<CreateRetailDiscountCodeRequest>(emptyCode);
  const [card, setCard] = useState<CreateRetailGiftCardRequest>(emptyCard);
  useEffect(() => {
    if (program.data) setSettings(program.data);
  }, [program.data]);
  const refresh = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["retail", "loyalty"] }),
      client.invalidateQueries({ queryKey: ["retail"] }),
    ]);
  };
  const saveSettings = useMutation({
    mutationFn: tenantWorkspaceApi.updateRetailLoyaltyProgram,
    onSuccess: async (data) => {
      setSettings(data);
      await refresh();
    },
  });
  const createCode = useMutation({
    mutationFn: tenantWorkspaceApi.createRetailDiscountCode,
    onSuccess: async () => {
      setCode(emptyCode());
      await refresh();
    },
  });
  const createCard = useMutation({
    mutationFn: tenantWorkspaceApi.createRetailGiftCard,
    onSuccess: async () => {
      setCard(emptyCard());
      await refresh();
    },
  });
  const updateCode = useMutation({
    mutationFn: ({
      item,
      active,
    }: {
      item: RetailDiscountCode;
      active: boolean;
    }) =>
      tenantWorkspaceApi.updateRetailDiscountCode(item.id, {
        name: item.name,
        customerId: item.customerId,
        discountType: item.discountType,
        value: item.value,
        maximumDiscountAmount: item.maximumDiscountAmount,
        minimumOrderAmount: item.minimumOrderAmount,
        currency: item.currency,
        startsAtUtc: item.startsAtUtc,
        expiresAtUtc: item.expiresAtUtc,
        maximumRedemptions: item.maximumRedemptions,
        isActive: active,
      }),
    onSuccess: refresh,
  });
  const updateCard = useMutation({
    mutationFn: ({ item, active }: { item: RetailGiftCard; active: boolean }) =>
      tenantWorkspaceApi.updateRetailGiftCard(item.id, {
        customerId: item.customerId,
        expiresAtUtc: item.expiresAtUtc,
        isActive: active,
      }),
    onSuccess: refresh,
  });
  const error =
    saveSettings.error ??
    createCode.error ??
    createCard.error ??
    updateCode.error ??
    updateCard.error;
  const saving =
    saveSettings.isPending ||
    createCode.isPending ||
    createCard.isPending ||
    updateCode.isPending ||
    updateCard.isPending;
  const currency = settings?.currency || "IRR";

  return (
    <div className="space-y-6 pb-12">
      <section className="rounded-3xl bg-gradient-to-bl from-violet-700 via-brand-700 to-slate-950 p-5 text-white shadow-lg sm:p-7">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div>
            <p className="text-sm font-bold text-violet-200">باشگاه مشتریان</p>
            <h1 className="mt-1 text-2xl font-black sm:text-3xl">
              وفاداری، اعتبار و تخفیف
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-white/75">
              امتیاز خرید، سطح VIP، کدهای تخفیف و کارت هدیه از این صفحه مدیریت
              می‌شوند و هنگام فروش سریع به‌صورت کنترل‌شده اعمال خواهند شد.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-[11px] text-white/60">کد فعال</p>
              <p className="mt-1 text-lg font-black">
                {number(
                  codes.data?.filter((item) => item.isAvailable).length ?? 0,
                )}
              </p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-[11px] text-white/60">کارت معتبر</p>
              <p className="mt-1 text-lg font-black">
                {number(
                  cards.data?.filter((item) => item.isAvailable).length ?? 0,
                )}
              </p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-[11px] text-white/60">هر امتیاز</p>
              <p className="mt-1 text-sm font-black">
                {settings
                  ? money(settings.pointRedemptionValue, currency)
                  : "—"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {error ? (
        <p className="rounded-2xl bg-rose-50 p-4 text-sm font-bold text-rose-700">
          {getErrorMessage(error)}
        </p>
      ) : null}

      <section className="grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-violet-50 text-violet-600">
              <AppIcon name="sparkles" size={19} />
            </span>
            <div>
              <h2 className="font-black text-ink-950">
                قوانین امتیاز و سطح VIP
              </h2>
              <p className="mt-1 text-xs leading-6 text-slate-400">
                مبنای کسب امتیاز و مرز هر سطح را برای فروشگاه خود تعیین کنید.
              </p>
            </div>
          </div>
          {settings ? (
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Toggle
                checked={settings.isEnabled}
                label="فعال بودن باشگاه مشتریان"
                onChange={(isEnabled) =>
                  setSettings((current) =>
                    current ? { ...current, isEnabled } : current,
                  )
                }
              />
              <Field label="واحد پول">
                <input
                  className="input font-sans"
                  dir="ltr"
                  maxLength={3}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            currency: event.target.value.toUpperCase(),
                          }
                        : current,
                    )
                  }
                  value={settings.currency}
                />
              </Field>
              <Field label="هر مبلغ خرید برای امتیاز">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={1}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            earningAmount: Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.earningAmount}
                />
              </Field>
              <Field label="تعداد امتیاز کسب‌شده">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={1}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            pointsPerEarning: Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.pointsPerEarning}
                />
              </Field>
              <Field label="ارزش هر امتیاز">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={1}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            pointRedemptionValue:
                              Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.pointRedemptionValue}
                />
              </Field>
              <Field label="حداقل امتیاز قابل مصرف">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={1}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            minimumPointsToRedeem:
                              Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.minimumPointsToRedeem}
                />
              </Field>
              <Field label="آستانه سطح نقره‌ای">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={0}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            silverSpendThreshold:
                              Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.silverSpendThreshold}
                />
              </Field>
              <Field label="آستانه سطح طلایی">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={0}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            goldSpendThreshold: Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.goldSpendThreshold}
                />
              </Field>
              <Field label="آستانه سطح پلاتینیوم">
                <input
                  className="input font-sans"
                  dir="ltr"
                  inputMode="numeric"
                  min={0}
                  onChange={(event) =>
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            platinumSpendThreshold:
                              Number(event.target.value) || 0,
                          }
                        : current,
                    )
                  }
                  value={settings.platinumSpendThreshold}
                />
              </Field>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-slate-400">
              در حال دریافت تنظیمات…
            </p>
          )}
          <button
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-3 text-sm font-black text-white disabled:opacity-50"
            disabled={!settings || saving}
            onClick={() => settings && saveSettings.mutate(settings)}
            type="button"
          >
            <AppIcon name="check" size={17} />
            {saveSettings.isPending ? "در حال ذخیره…" : "ذخیره قوانین"}
          </button>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="font-black text-ink-950">راهنمای استفاده در فروش</h2>
          <div className="mt-5 space-y-3 text-sm leading-7 text-slate-600">
            <Step
              index="۱"
              text="در میز فروش سریع، کد تخفیف، تعداد امتیاز و کد کارت هدیه را وارد کنید."
            />
            <Step
              index="۲"
              text="سیستم تاریخ، سقف استفاده، حداقل خرید، اعتبار کارت و موجودی امتیاز را کنترل می‌کند."
            />
            <Step
              index="۳"
              text="پس از ثبت فاکتور، مانده امتیاز و اعتبار کارت در پرونده مشتری و دفتر گردش ثبت می‌شود."
            />
            <Step
              index="۴"
              text="در مرجوعی، امتیاز متناسب با مبلغ بازگشتی اصلاح می‌شود تا سطح VIP قابل اعتماد بماند."
            />
          </div>
        </article>
      </section>

      <section className="grid gap-5 xl:grid-cols-2">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
              <AppIcon name="document" size={19} />
            </span>
            <div>
              <h2 className="font-black text-ink-950">ایجاد کد تخفیف</h2>
              <p className="mt-1 text-xs text-slate-400">
                کد عمومی بسازید یا شناسه مشتری را برای کد اختصاصی وارد کنید.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Field label="عنوان">
              <input
                className="input"
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                value={code.name}
              />
            </Field>
            <Field label="کد">
              <input
                className="input font-sans uppercase"
                dir="ltr"
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    code: event.target.value,
                  }))
                }
                value={code.code}
              />
            </Field>
            <Field label="مشتری اختصاصی">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    customerId: event.target.value || null,
                  }))
                }
                value={code.customerId ?? ""}
              >
                <option value="">عمومی؛ قابل استفاده برای همه</option>
                {customers.data?.items.map((customer) => {
                  const name =
                    [customer.firstName, customer.lastName]
                      .filter(Boolean)
                      .join(" ") || "مشتری بدون نام";
                  const mobile = customer.contactPoints.find(
                    (point) => point.type === "mobile",
                  )?.value;
                  return (
                    <option key={customer.id} value={customer.id}>
                      {name}
                      {mobile ? ` · ${mobile}` : ""}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="نوع">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    discountType: event.target
                      .value as CreateRetailDiscountCodeRequest["discountType"],
                  }))
                }
                value={code.discountType}
              >
                <option value="Percentage">درصدی</option>
                <option value="FixedAmount">مبلغ ثابت</option>
              </select>
            </Field>
            <Field
              label={
                code.discountType === "Percentage" ? "درصد تخفیف" : "مبلغ تخفیف"
              }
            >
              <input
                className="input font-sans"
                dir="ltr"
                inputMode="numeric"
                min={1}
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    value: Number(event.target.value) || 0,
                  }))
                }
                value={code.value || ""}
              />
            </Field>
            <Field label="حداقل مبلغ خرید">
              <input
                className="input font-sans"
                dir="ltr"
                inputMode="numeric"
                min={0}
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    minimumOrderAmount: Number(event.target.value) || 0,
                  }))
                }
                value={code.minimumOrderAmount || ""}
              />
            </Field>
            <Field label="سقف تعداد استفاده">
              <input
                className="input font-sans"
                dir="ltr"
                inputMode="numeric"
                min={1}
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    maximumRedemptions: event.target.value
                      ? Number(event.target.value)
                      : null,
                  }))
                }
                value={code.maximumRedemptions ?? ""}
              />
            </Field>
            <Field label="سقف مبلغ تخفیف">
              <input
                className="input font-sans"
                dir="ltr"
                inputMode="numeric"
                min={1}
                onChange={(event) =>
                  setCode((current) => ({
                    ...current,
                    maximumDiscountAmount: event.target.value
                      ? Number(event.target.value)
                      : null,
                  }))
                }
                value={code.maximumDiscountAmount ?? ""}
              />
            </Field>
            <Field label="انقضا">
              <JalaliDateTimePicker
                onChange={(value) =>
                  setCode((current) => ({
                    ...current,
                    expiresAtUtc: value?.toISOString() ?? null,
                  }))
                }
                placeholder="تاریخ و ساعت انقضا را به شمسی انتخاب کنید"
                value={code.expiresAtUtc ? dayjs(code.expiresAtUtc) : undefined}
              />
            </Field>
          </div>
          <button
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white disabled:opacity-50"
            disabled={
              saving ||
              !code.code.trim() ||
              !code.name.trim() ||
              code.value <= 0
            }
            onClick={() => createCode.mutate(code)}
            type="button"
          >
            <AppIcon name="plus" size={17} />
            ایجاد کد تخفیف
          </button>
          <div className="mt-6 space-y-2 border-t border-slate-100 pt-4">
            {codes.data?.map((item) => (
              <DiscountRow
                item={item}
                key={item.id}
                loading={updateCode.isPending}
                onToggle={(active) => updateCode.mutate({ item, active })}
              />
            )) ?? null}
            {!codes.data?.length ? (
              <p className="py-4 text-center text-xs font-bold text-slate-400">
                هنوز کد تخفیفی تعریف نشده است.
              </p>
            ) : null}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-amber-50 text-amber-600">
              <AppIcon name="sparkles" size={19} />
            </span>
            <div>
              <h2 className="font-black text-ink-950">صدور کارت هدیه</h2>
              <p className="mt-1 text-xs text-slate-400">
                کارت هدیه اعتبار ذخیره‌شده دارد و مانند یک روش پرداخت در فروش
                سریع استفاده می‌شود.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Field label="کد کارت">
              <input
                className="input font-sans uppercase"
                dir="ltr"
                onChange={(event) =>
                  setCard((current) => ({
                    ...current,
                    code: event.target.value,
                  }))
                }
                value={card.code}
              />
            </Field>
            <Field label="اعتبار اولیه">
              <input
                className="input font-sans"
                dir="ltr"
                inputMode="numeric"
                min={1}
                onChange={(event) =>
                  setCard((current) => ({
                    ...current,
                    initialBalance: Number(event.target.value) || 0,
                  }))
                }
                value={card.initialBalance || ""}
              />
            </Field>
            <Field label="مشتری اختصاصی">
              <select
                className="input bg-white"
                onChange={(event) =>
                  setCard((current) => ({
                    ...current,
                    customerId: event.target.value || null,
                  }))
                }
                value={card.customerId ?? ""}
              >
                <option value="">عمومی؛ قابل استفاده برای همه</option>
                {customers.data?.items.map((customer) => {
                  const name =
                    [customer.firstName, customer.lastName]
                      .filter(Boolean)
                      .join(" ") || "مشتری بدون نام";
                  const mobile = customer.contactPoints.find(
                    (point) => point.type === "mobile",
                  )?.value;
                  return (
                    <option key={customer.id} value={customer.id}>
                      {name}
                      {mobile ? ` · ${mobile}` : ""}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="انقضا">
              <JalaliDateTimePicker
                onChange={(value) =>
                  setCard((current) => ({
                    ...current,
                    expiresAtUtc: value?.toISOString() ?? null,
                  }))
                }
                placeholder="تاریخ و ساعت انقضا را به شمسی انتخاب کنید"
                value={card.expiresAtUtc ? dayjs(card.expiresAtUtc) : undefined}
              />
            </Field>
            <Field label="واحد پول">
              <input
                className="input font-sans"
                dir="ltr"
                maxLength={3}
                onChange={(event) =>
                  setCard((current) => ({
                    ...current,
                    currency: event.target.value.toUpperCase(),
                  }))
                }
                value={card.currency}
              />
            </Field>
          </div>
          <button
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-black text-slate-950 disabled:opacity-50"
            disabled={saving || !card.code.trim() || card.initialBalance <= 0}
            onClick={() => createCard.mutate(card)}
            type="button"
          >
            <AppIcon name="plus" size={17} />
            صدور کارت هدیه
          </button>
          <div className="mt-6 space-y-2 border-t border-slate-100 pt-4">
            {cards.data?.map((item) => (
              <GiftCardRow
                item={item}
                key={item.id}
                loading={updateCard.isPending}
                onToggle={(active) => updateCard.mutate({ item, active })}
              />
            )) ?? null}
            {!cards.data?.length ? (
              <p className="py-4 text-center text-xs font-bold text-slate-400">
                هنوز کارت هدیه‌ای صادر نشده است.
              </p>
            ) : null}
          </div>
        </article>
      </section>
    </div>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement => (
  <label>
    <span className="mb-1.5 block text-xs font-bold text-slate-500">
      {label}
    </span>
    {children}
  </label>
);
const Toggle = ({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (value: boolean) => void;
}): React.ReactElement => (
  <button
    aria-checked={checked}
    className="flex min-h-11 items-center justify-between rounded-xl border border-slate-200 px-3 text-right"
    onClick={() => onChange(!checked)}
    role="switch"
    type="button"
  >
    <span className="text-xs font-black text-slate-700">{label}</span>
    <span
      className={`relative h-6 w-11 rounded-full transition ${checked ? "bg-violet-600" : "bg-slate-200"}`}
    >
      <span
        className={`absolute top-1 size-4 rounded-full bg-white shadow transition ${checked ? "left-1" : "left-6"}`}
      />
    </span>
  </button>
);
const Step = ({
  index,
  text,
}: {
  index: string;
  text: string;
}): React.ReactElement => (
  <div className="flex gap-3 rounded-2xl bg-slate-50 p-3">
    <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-violet-100 text-xs font-black text-violet-700">
      {index}
    </span>
    <p>{text}</p>
  </div>
);
const DiscountRow = ({
  item,
  onToggle,
  loading,
}: {
  item: RetailDiscountCode;
  onToggle: (active: boolean) => void;
  loading: boolean;
}): React.ReactElement => (
  <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 p-3">
    <div className="min-w-0">
      <p className="truncate text-xs font-black text-slate-800">
        {item.name}{" "}
        <span className="font-sans text-brand-600">{item.code}</span>
      </p>
      <p className="mt-1 text-[11px] text-slate-500">
        {item.discountType === "Percentage"
          ? `${number(item.value)}٪`
          : money(item.value, item.currency)}{" "}
        · {number(item.redemptionCount)} استفاده
        {item.expiresAtUtc ? ` · تا ${formatDateTime(item.expiresAtUtc)}` : ""}
      </p>
    </div>
    <button
      className={`rounded-lg px-2.5 py-1.5 text-[11px] font-black disabled:opacity-50 ${item.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
      disabled={loading}
      onClick={() => onToggle(!item.isActive)}
      type="button"
    >
      {item.isActive ? "فعال" : "غیرفعال"}
    </button>
  </div>
);
const GiftCardRow = ({
  item,
  onToggle,
  loading,
}: {
  item: RetailGiftCard;
  onToggle: (active: boolean) => void;
  loading: boolean;
}): React.ReactElement => (
  <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 p-3">
    <div className="min-w-0">
      <p className="truncate font-sans text-xs font-black text-slate-800">
        {item.code}
      </p>
      <p className="mt-1 text-[11px] text-slate-500">
        مانده: {money(item.balance, item.currency)}
        {item.expiresAtUtc ? ` · تا ${formatDateTime(item.expiresAtUtc)}` : ""}
      </p>
    </div>
    <button
      className={`rounded-lg px-2.5 py-1.5 text-[11px] font-black disabled:opacity-50 ${item.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
      disabled={loading}
      onClick={() => onToggle(!item.isActive)}
      type="button"
    >
      {item.isActive ? "فعال" : "غیرفعال"}
    </button>
  </div>
);
